import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  Circle,
  Polygon,
} from "@react-google-maps/api";

import { useSelector, useDispatch } from "react-redux";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import axios from "axios";
import { GOOGLE_MAPS_API_KEY, GOOGLE_MAPS_LIBRARIES } from "../../../../config";
import { addGeofence } from "config/apis";
import {
  clearLocationRange,
  clearSelectedLocation,
} from "features/geoFence/geoFenceSlice";
import { setRefresh } from "features/geoFence/geoFenceSlice";
import { setLocationRange } from "features/geoFence/geoFenceSlice";
import { useMaterialUIController } from "context";

const containerStyle = {
  width: "100%",
  height: "100%",
};

const defaultCenter = {
  lat: 20.5937,
  lng: 78.9629,
};

function GoogleMapView() {
  const mapRef = useRef(null);
  const polygonRef = useRef(null);
  const hasAutoFocusedRef = useRef(false);
  const token = localStorage.getItem("token");
  const dispatch = useDispatch();

  // Dark Mode Support
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const selectedLocation = useSelector((state) => state.geoFence.selectedLocation);
  const allLocationRanges = useSelector((state) => state.geoFence.allLocationRanges);
  const locationRange = useSelector((state) => state.geoFence.locationRange);
  const refresh = useSelector((state) => state.geoFence.refresh);

  const [geofenceData, setGeofenceData] = useState({
    tags: "",
    radius: "",
    adminOnly: false,
  });
  const [usePolygon, setUsePolygon] = useState(false);
  const [polygonCoords, setPolygonCoords] = useState([]);

  // 🚀 DRAG LOGIC STATE (Custom Draggable Hook Implementation)
  const [dragState, setDragState] = useState({
    isDragging: false,
    startX: 0,
    startY: 0,
    x: 0,
    y: 0,
  });

  // Handle Drag Start
  const handleMouseDown = useCallback((e) => {
    setDragState((prev) => ({
      ...prev,
      isDragging: true,
      startX: e.clientX - prev.x,
      startY: e.clientY - prev.y,
    }));
  }, []);

  // Handle Drag Move & End via Window listeners (For smooth dragging outside card)
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!dragState.isDragging) return;
      setDragState((prev) => ({
        ...prev,
        x: e.clientX - prev.startX,
        y: e.clientY - prev.startY,
      }));
    };

    const handleMouseUp = () => {
      setDragState((prev) => ({ ...prev, isDragging: false }));
    };

    if (dragState.isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    } else {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    }

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [dragState.isDragging]);

  // Setup refs for Polygon editing
  const onPolygonLoad = useCallback((polygon) => {
    polygonRef.current = polygon;
  }, []);

  const onPolygonUnmount = useCallback(() => {
    polygonRef.current = null;
  }, []);

  useEffect(() => {
    if (selectedLocation) {
      setGeofenceData({
        tags: selectedLocation.filterId ? selectedLocation.tags ?? "" : "",
        radius: selectedLocation.filterId ? selectedLocation.radius ?? "" : "",
        adminOnly: selectedLocation.adminOnly || false,
      });

      if (selectedLocation.type === "POLYGON" && selectedLocation.coordinates) {
        setUsePolygon(true);
        setPolygonCoords(selectedLocation.coordinates);
      } else {
        setUsePolygon(false);
        setPolygonCoords([]);
      }
    }
  }, [selectedLocation]);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const onLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  const onUnmount = useCallback(() => {
    mapRef.current = null;
  }, []);

  // 🗺️ Saare geofences ko cover karte hue map ko fit/zoom karne wala reusable
  // function. Ye 2 jagah use hota hai: (1) page load par ek baar, (2) jab bhi
  // koi bhi geofence hover/selected na ho (mouse kisi list-item se hat jaye).
  const fitAllBounds = useCallback(() => {
    if (
      !isLoaded ||
      !mapRef.current ||
      !allLocationRanges ||
      allLocationRanges.length === 0
    ) {
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    let pointsAdded = 0;

    allLocationRanges.forEach((item) => {
      if (item.type === "LocationFilter" && item.data?.location) {
        const { latitude, longitude } = item.data.location;
        bounds.extend({ lat: latitude, lng: longitude });
        pointsAdded++;
      } else if (item.type === "POLYGON" && Array.isArray(item.data?.coordinates)) {
        item.data.coordinates.forEach(([lng, lat]) => {
          bounds.extend({ lat, lng });
          pointsAdded++;
        });
      }
    });

    if (pointsAdded === 0) return;

    mapRef.current.fitBounds(bounds);

    // Agar sirf ek hi geofence hai to fitBounds bahut zyada zoom-in kar deta hai
    // (kabhi-kabhi zoom 21 tak) — usko ek reasonable zoom level (16) par cap kar rahe hain.
    window.google.maps.event.addListenerOnce(mapRef.current, "bounds_changed", () => {
      if (mapRef.current.getZoom() > 16) {
        mapRef.current.setZoom(16);
      }
    });
  }, [allLocationRanges, isLoaded]);

  // 🎯 COMPONENT LOAD PAR PEHLI BAAR FOCUS: jaise hi existing geofences ka data
  // (allLocationRanges) load ho jaye aur map ready ho, map ko un sab geofences
  // ko cover karte hue auto zoom/pan karwao. Yeh sirf EK BAAR chalega
  // (hasAutoFocusedRef ki wajah se) — uske baad neeche wala hover-based effect
  // sambhal lega.
  useEffect(() => {
    if (hasAutoFocusedRef.current || !isLoaded || !mapRef.current) return;
    fitAllBounds();
    hasAutoFocusedRef.current = true;
  }, [allLocationRanges, isLoaded, fitAllBounds]);

  // 🖱️ HOVER-OUT FOCUS: jab koi bhi geofence hover/selected nahi hai (list se
  // mouse hat gaya), map wapas saare geofences ko fit karke dikhaye.
  useEffect(() => {
    if (!hasAutoFocusedRef.current) return; // pehla auto-focus hone dein pehle
    if (!selectedLocation && !locationRange) {
      fitAllBounds();
    }
  }, [selectedLocation, locationRange, fitAllBounds]);

  const center = useMemo(() => {
    if (selectedLocation) {
      return { lat: selectedLocation.lat, lng: selectedLocation.lng };
    }
    if (locationRange) {
      return { lat: locationRange.lat, lng: locationRange.lng };
    }
    return defaultCenter;
  }, [selectedLocation?.lat, selectedLocation?.lng, locationRange?.lat, locationRange?.lng]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setGeofenceData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleLocation = (fence) => {
    dispatch(clearSelectedLocation());
    dispatch(setLocationRange(null));
    dispatch(
      setLocationRange({
        lat: fence.latitude,
        longitude: fence.longitude,
        filterId: fence.filterId,
        radius: fence.radius,
      })
    );
  };

  const handleMapClick = useCallback(
    (event) => {
      if (usePolygon) {
        const lat = event.latLng.lat();
        const lng = event.latLng.lng();
        setPolygonCoords((prev) => [...prev, [lng, lat]]);
      }
    },
    [usePolygon]
  );

  const handleAddGeofence = async () => {
    if (!selectedLocation && !usePolygon) {
      alert("Please select a location first.");
      return;
    }

    const headers = { "x-dhundhoo-session": token };

    try {
      let response;

      if (usePolygon) {
        let currentCoords = polygonCoords;
        if (polygonRef.current) {
          const path = polygonRef.current.getPath().getArray();
          currentCoords = path.map((latLng) => [latLng.lng(), latLng.lat()]);
        }

        if (currentCoords.length < 3) {
          alert("Please draw at least 3 points on the map to form a polygon.");
          return;
        }

        const finalCoords = [...currentCoords];
        const firstP = finalCoords[0];
        const lastP = finalCoords[finalCoords.length - 1];
        if (firstP[0] !== lastP[0] || firstP[1] !== lastP[1]) {
          finalCoords.push([...firstP]);
        }

        const payload = {
          tags: geofenceData.tags,
          locationType: "INSTITUTE",
          adminOnly: geofenceData.adminOnly,
          coordinates: finalCoords,
          ...(selectedLocation?.filterId && {
            filterId: selectedLocation.filterId,
          }),
        };

        response = await axios.post(
          "https://v3.dhundhoo.com/thing/addGeoFence/polygon",
          payload,
          { headers }
        );

      } else {
        const payload = {
          latitude: selectedLocation.lat,
          longitude: selectedLocation.lng,
          locationType: "INSTITUTE",
          radius: geofenceData.radius,
          tags: geofenceData.tags,
          ...(selectedLocation?.filterId && {
            filterId: selectedLocation.filterId,
          }),
          adminOnly: geofenceData.adminOnly,
        };

        response = await axios.post(addGeofence, payload, { headers });
      }

      setGeofenceData({ tags: "", radius: "", adminOnly: false });
      setPolygonCoords([]);
      setUsePolygon(false);
      dispatch(clearSelectedLocation());
    } catch (error) {
      console.error("Error saving geofence:", error);
      alert("Failed to save geofence. Please try again.");
    }

    dispatch(setRefresh(!refresh));
  };

  useEffect(() => {
    dispatch(clearLocationRange());
    dispatch(clearSelectedLocation());
  }, []);

  const commonShapeOptions = {
    fillColor: "#000000",
    fillOpacity: 0.35,
    strokeColor: "#000000",
    strokeOpacity: 0.8,
    strokeWeight: 2,
  };

  return (
    <MDBox sx={{ width: "100%", height: "100%", borderRadius: 2, overflow: "hidden", position: "relative" }}>
      {isLoaded ? (
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={center}
          zoom={selectedLocation || locationRange ? 15 : 5}
          onLoad={onLoad}
          onUnmount={onUnmount}
          onClick={handleMapClick}
          options={{
            mapTypeControl: false,
            mapTypeId: "satellite",
            draggableCursor: usePolygon ? "crosshair" : "grab",
            streetViewControl: false,
            keyboardShortcuts: false,
          }}
        >
          {!selectedLocation &&
            allLocationRanges.map((item, idx) => {
              const key = item.filterId || idx;

              if (item.type === "LocationFilter" && item.data?.location) {
                const { latitude, longitude } = item.data.location;
                return (
                  <React.Fragment key={key}>
                    <Marker position={{ lat: latitude, lng: longitude }} onClick={() => handleLocation(item)} />
                    <Circle center={{ lat: latitude, lng: longitude }} radius={Number(item.data.radius) || 0} options={commonShapeOptions} />
                  </React.Fragment>
                );
              }
              if (item.type === "POLYGON" && Array.isArray(item.data?.coordinates)) {
                const path = item.data.coordinates.map(([lng, lat]) => ({ lat, lng }));

                const n = item.data.coordinates.length;
                const sum = item.data.coordinates.reduce(
                  (acc, [lng, lat]) => { acc.lng += lng; acc.lat += lat; return acc; },
                  { lng: 0, lat: 0 }
                );
                const center = { lat: sum.lat / n, lng: sum.lng / n };

                return (
                  <React.Fragment key={key}>
                    <Marker position={center} onClick={() => handleLocation(item)} />
                    <Polygon key={key} path={path} options={commonShapeOptions} />
                  </React.Fragment>
                );
              }
              return null;
            })}

          {polygonCoords.length > 0 && (
            <Polygon
              onLoad={onPolygonLoad}
              onUnmount={onPolygonUnmount}
              path={polygonCoords.map(([lng, lat]) => ({ lat, lng }))}
              options={{
                ...commonShapeOptions,
                editable: usePolygon,
                draggable: usePolygon,
              }}
            />
          )}

          {(selectedLocation || (locationRange && locationRange.radius)) && !usePolygon && (
            <React.Fragment>
              <Marker
                position={{
                  lat: selectedLocation ? selectedLocation.lat : locationRange.lat,
                  lng: selectedLocation ? selectedLocation.lng : locationRange.lng,
                }}
              />
              <Circle
                center={{
                  lat: selectedLocation ? selectedLocation.lat : locationRange.lat,
                  lng: selectedLocation ? selectedLocation.lng : locationRange.lng,
                }}
                radius={Number(selectedLocation ? geofenceData.radius : locationRange.radius)}
                options={commonShapeOptions}
              />
            </React.Fragment>
          )}
        </GoogleMap>
      ) : (
        <MDBox display="flex" alignItems="center" justifyContent="center" height="100%">
          <MDTypography variant="body1">Loading Map...</MDTypography>
        </MDBox>
      )}

      {/* 🚀 DRAGGABLE FLOATING UPDATE CARD */}
      {selectedLocation && !locationRange && (
        <MDBox
          bgColor={darkMode ? "dark" : "white"}
          sx={{
            position: "absolute",
            bottom: "40px", // Base position
            left: "50%",    // Base position
            transform: `translate(calc(-50% + ${dragState.x}px), ${dragState.y}px)`, // Dynamic translation for drag
            background: darkMode ? "#1f283e !important" : "#ffffff !important",
            padding: "20px",
            borderRadius: "12px",
            boxShadow: dragState.isDragging
              ? "0px 25px 50px rgba(0,0,0,0.6) !important" // Deeper shadow when dragged
              : "0px 15px 40px rgba(0,0,0,0.4) !important",
            width: "320px",
            zIndex: 99999,
            transition: dragState.isDragging ? "none" : "box-shadow 0.2s ease-in-out", // Remove transition while dragging for instant response
          }}
        >
          {/* DRAG HANDLE (Title Area) */}
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
            onMouseDown={handleMouseDown} // 👈 Event to start drag
            sx={{
              cursor: dragState.isDragging ? "grabbing" : "grab",
              userSelect: "none", // Avoid text selection while dragging
              padding: "5px 10px",
              backgroundColor: darkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)", // Light highlight
              borderRadius: "8px"
            }}
          >
            <MDTypography variant="h6" color={darkMode ? "white" : "dark"}>
              ✋ {selectedLocation.filterId ? "Update Geofence" : "Add Geofence"}
            </MDTypography>

            {/* ❌ CLOSE BUTTON */}
            <MDTypography
              variant="h5"
              color={darkMode ? "white" : "dark"}
              sx={{
                cursor: "pointer",
                lineHeight: 1,
                padding: "0 5px"
              }}
              onMouseDown={(e) => e.stopPropagation()} // 👈 CRITICAL: Prevents drag from starting
              onClick={() => {
                // Clear Redux state to close the modal
                dispatch(clearSelectedLocation());
                // Reset local form states
                setUsePolygon(false);
                setPolygonCoords([]);
                setGeofenceData({ tags: "", radius: "", adminOnly: false });
              }}
            >
              &times;
            </MDTypography>
          </MDBox>

          <MDBox sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <MDInput
              fullWidth
              placeholder="Name For Geofence"
              variant="outlined"
              name="tags"
              value={geofenceData.tags}
              onChange={handleInputChange}
            />

            <MDBox display="flex" alignItems="center">
              <input
                type="checkbox"
                checked={usePolygon}
                onChange={(e) => {
                  setUsePolygon(e.target.checked);
                  if (!e.target.checked) setPolygonCoords([]);
                }}
                style={{ width: "16px", height: "16px", cursor: "pointer" }}
              />
              <MDTypography variant="button" ml={1} color={darkMode ? "white" : "text"}>
                Use Polygon
              </MDTypography>
            </MDBox>

            {usePolygon && (
              <MDTypography variant="caption" color="error" sx={{ fontStyle: "italic", mt: -1 }}>
                {selectedLocation.filterId
                  ? "Drag points on the map to edit."
                  : "Click on the map to draw points."}
              </MDTypography>
            )}

            {!usePolygon && (
              <MDInput
                fullWidth
                type="number"
                placeholder="Radius (meters)"
                variant="outlined"
                name="radius"
                value={geofenceData.radius}
                onChange={handleInputChange}
              />
            )}
          </MDBox>

          <MDBox display="flex" alignItems="center" mt={1} mb={2}>
            <input
              type="checkbox"
              name="adminOnly"
              checked={geofenceData.adminOnly}
              onChange={handleInputChange}
              style={{ width: "16px", height: "16px", cursor: "pointer" }}
            />
            <MDTypography variant="button" ml={1} color={darkMode ? "white" : "text"}>
              Admin Only
            </MDTypography>
          </MDBox>

          <MDButton variant="contained" color="primary" fullWidth sx={{ borderRadius: "8px", color: "#fff !important" }} onClick={handleAddGeofence}>
            {selectedLocation.filterId ? "Save Changes" : "Save Geofence"}
          </MDButton>

          {usePolygon && polygonCoords.length > 0 && (
            <MDButton variant="outlined" color="secondary" fullWidth sx={{ borderRadius: "8px", mt: 1 }} onClick={() => setPolygonCoords([])}>
              Clear Drawing
            </MDButton>
          )}
        </MDBox>
      )}
    </MDBox>
  );
}

export default GoogleMapView;